import { Widget } from "@foretagsplatsen/widgetjs";
import datePickerWidget from "../import/datePickerWidget.js";
import Timespan from "../../../models/period/Timespan.js";

/**
 * `dateRangePickerWidget` is a widget used to pick a date range.
 *
 * It will popup two calendars to choose the two dates.
 * It also supports mobile devices.
 *
 * @param {Date} [minDate]    - Minimal date
 * @param {Date} [maxDate]    - Maximal date
 * @param {Date} [starDate]   - Selected start date
 * @param {Date} [endDate]    - Selected end date
 * @param {boolean} [triggerOnChange] - When true, an event will be triggered on change
 * @param {string} [cssClass] - Extra css classes to attach to the inputs
 */
export default class DateRangePickerWidget extends Widget {
	constructor({
		cssClass = "",
		triggerOnChange = false,
		startDate = new Date(0),
		endDate = new Date(),
		minDate = startDate,
		maxDate = endDate,
	}) {
		super(...arguments);

		this._cssClass = cssClass;
		this._triggerOnChange = triggerOnChange;

		this.onDateRangeChanged = this._events.createEvent();

		this._startPicker = datePickerWidget({ name: "start" });

		this._startPicker.onDateChanged.register((date) => {
			this._startDate = date;
			this._triggerChange();
		});

		this._endPicker = datePickerWidget({ name: "end" });

		this._endPicker.onDateChanged.register((date) => {
			this._endDate = date;
			this._triggerChange();
		});

		this.setDateRange({ startDate, endDate, minDate, maxDate });
	}

	getDateRange() {
		return {
			startDate: this._startDate,
			endDate: this._endDate,
		};
	}

	asTimespan() {
		const { startDate: start, endDate: end } = this.getDateRange();
		return new Timespan({ start, end });
	}

	getStartDate() {
		return this._startDate;
	}

	getEndDate() {
		return this._endDate;
	}

	setDateRange({ startDate, endDate, minDate, maxDate }) {
		this._startDate = startDate || this._startDate;
		this._endDate = endDate || this._endDate;
		this._minDate = minDate || this._minDate;
		this._maxDate = maxDate || this._maxDate;

		this._startPicker.setRange(
			new Timespan({ start: this._minDate, end: this._maxDate }),
		);

		this._startPicker.setSelectedDate(this._startDate);

		this._endPicker.setRange(
			new Timespan({ start: this._minDate, end: this._maxDate }),
		);

		this._endPicker.setSelectedDate(this._endDate);

		this.update();
	}

	renderContentOn(html) {
		html.div(
			{ class: ["input-daterange", this._cssClass] },
			this._startPicker,
			html.span({ class: "add-on" }, "-"),
			this._endPicker,
		);
	}

	_triggerChange() {
		if (!this._triggerOnChange) {
			return;
		}
		this.onDateRangeChanged.trigger(this.getDateRange());
	}
}
